import { jwtDecode } from 'jwt-decode';
import { apiBaseUrl } from '../apiConsts';

import { getDefaultError } from '../../utils';
import { getDataWithRetry, sendWithoutBody } from '../fetchUtils';

import type { FetchResult } from '../fetchUtils';
import type { BaseApiResponse } from '../apiTypes';

export const sendVerificationCode = async (
  email: string,
): Promise<FetchResult<null>> => {
  if (!email) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No email provided'),
    };
  }
  return sendWithoutBody(`${apiBaseUrl}auth/send-code`, {
    headers: { 'content-type': 'application/json' },
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

export interface VerificationCodeResponse extends BaseApiResponse {
  managerId?: string;
  trainerId?: string;
  memberId?: string;
  loginExpiration?: number;
}

enum LoginRole {
  MEMBER = 'ROLE_MEMBER',
  TRAINER = 'ROLE_TRAINER',
  MANAGER = 'ROLE_MANAGER',
}

interface LoginRoleData {
  role: LoginRole;
  id: string;
}

interface VerifyLoginJwtPayload {
  sub: string; //the email
  roles: LoginRoleData[];
  iat: number;
  exp: number;
}

export const verifyLoginCode = async (
  email: string,
  code: string,
): Promise<FetchResult<VerificationCodeResponse | null>> => {
  if (!email || !code) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No email or code provided'),
    };
  }
  const isVerificationResponse = (
    payload: unknown,
  ): payload is { jwtToken: string } => {
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'jwtToken' in payload &&
      typeof (payload as { jwtToken?: unknown }).jwtToken === 'string'
    );
  };

  const response = await getDataWithRetry<{ jwtToken: string }>(
    `${apiBaseUrl}auth/verify-code`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ email, code }),
    },
    isVerificationResponse,
  );
  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error: response.error,
      status: response.status,
      durationMs: response.durationMs,
    };
  }
  const jwtToken = response.data.jwtToken;
  const { roles, exp }: VerifyLoginJwtPayload = jwtDecode(jwtToken);
  const managerId = roles.find(({ role }) => role === LoginRole.MANAGER)?.id;
  const trainerId = roles.find(({ role }) => role === LoginRole.TRAINER)?.id;
  const memberId = roles.find(({ role }) => role === LoginRole.MEMBER)?.id;

  const parsedResponse: VerificationCodeResponse = {
    success: true,
    managerId,
    trainerId,
    memberId,
    loginExpiration: exp,
  };

  return {
    success: true,
    data: parsedResponse,
    status: response.status,
    durationMs: response.durationMs,
  };
};
